import {
  Box,
  Button,
  Chip,
  CssBaseline,
  Divider,
  Grid,
  Paper,
  Stack,
  SvgIcon,
  ThemeProvider,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { fetchPlaiditems } from "../../api/plaid";
import { LinkPlaidButton } from "../plaid/linkPlaid";
import { muiTheme } from "../welcomeSetup/theme/theme";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";

import { useEffect } from "react";

import { fetchCustomerAccounts } from "../../api/accounts";
import { Context } from "../../ContextWrapper";
import { AccessToken } from "../../utils";

import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import axios from "axios";
import { fetchCurrentProfile } from "../../api/profile";

const ClientPlaidItemDelete = ({
  customer_id,
  plaiditem_id,
  removePlaidItemFromList,
  setProfile,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await axios.delete(
        `/api/v2/customers/${customer_id}/plaiditems/${plaiditem_id}/`
      );

      if (response.status === 200) {
        removePlaidItemFromList(plaiditem_id);

        const currentProfile = await fetchCurrentProfile();
        console.log("Fetched profile:", currentProfile);

        setProfile(currentProfile[0]);

        handleClose();
      } else {
        console.error("Unexpected response code:", response.status);
      }
    } catch (error) {
      console.error("Error deleting the bank item:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        fullWidth
        onClick={handleOpen}
        startIcon={<DeleteIcon />}
        sx={{
          fontFamily: "Lato",
          backgroundColor: "#536DEC",
          color: "white",
          fontWeight: 600,
          textTransform: "none",
          borderRadius: "8px",
          "&:hover": { backgroundColor: "#455FDE" },
          "&:active": { backgroundColor: "#3E55C8" },
          py: 1.2,
        }}
      >
        Delete
      </Button>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle sx={{ textTransform: "none", fontFamily: "Lato" }}>
          Confirm Delete
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ textTransform: "none", fontFamily: "Lato" }}>
            Are you sure you want to delete this plaid account?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{ textTransform: "none", fontFamily: "Lato" }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={loading}
            sx={{
              fontFamily: "Lato",
              borderRadius: "8px",
              backgroundColor: "#536DEC",
              "&:hover": { backgroundColor: "#455FDE" },
              "&:active": { backgroundColor: "#3E55C8" },
              color: "white",
              minWidth: 100,
              textTransform: "none",
            }}
            startIcon={
              loading ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {loading ? "Processing..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const PlaidItem = ({
  item,
  customer_id,
  removePlaidItemFromList,
  refreshPlaidItemList,
  customer,
  setProfile,
}) => {
  const hasError = item.plaiditemerror_set.some(
    (err) =>
      err.code === "ITEM_LOGIN_REQUIRED" ||
      err.code === "USER_PERMISSION_REVOKED"
  );

  const isProcessing = item.transactions_processing_status === "processing";

  return (
    <Paper
      elevation={1}
      sx={{
        width: "100%",
        p: 2,
        borderRadius: 3,
        border: "1px solid rgba(0,0,0,0.08)",
        background: "#fff",
        mb: 3,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          p: 1,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <SvgIcon>
            <image href="/emojione-v1_bank.svg" width="24" height="24" />
          </SvgIcon>

          <Box>
            <Typography
              fontSize={"22px"}
              fontWeight={800}
              sx={{ fontFamily: "Lato" }}
            >
              {item.name}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {hasError &&
            item.plaiditemerror_set?.map((error) => (
              <Tooltip key={error.id} title={error.message} arrow>
                <SvgIcon>
                  <image href="/link-issue.svg" width="24" height="24" />
                </SvgIcon>
              </Tooltip>
            ))}
        </Stack>
      </Stack>

      <Box>
        <TableContainer
          component={Paper}
          sx={{
            width: "100%",
            maxWidth: "100%",
            margin: 0,
            boxShadow: "none",
          }}
        >
          <Table
            sx={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow sx={{ borderBottom: "1px solid #536DEC" }}>
                <TableCell
                  sx={{
                    fontSize: 15,
                    fontWeight: "bold",
                    borderBottom: "none",
                    color: "#536DEC",
                    p: "10px 5px",
                    fontFamily: "Lato",
                  }}
                >
                  Account Name
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 15,
                    fontWeight: "bold",
                    borderBottom: "none",
                    color: "#536DEC",
                    p: "10px 5px",
                    fontFamily: "Lato",
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 15,
                    fontWeight: "bold",
                    borderBottom: "none",
                    color: "#536DEC",
                    p: "10px 5px",
                    fontFamily: "Lato",
                  }}
                >
                  Status
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {item.bankaccount_set.map((account) => (
                <TableRow
                  key={account.id}
                  sx={{
                    borderBottom: "none",
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: 16,
                      borderBottom: "none",
                      fontFamily: "Lato",
                      p: "10px 5px",
                    }}
                  >
                    {account.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 16,
                      borderBottom: "none",
                      fontFamily: "Lato",
                      p: "10px 5px",
                    }}
                  >
                    {account.subtype}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 16,
                      borderBottom: "none",
                      fontFamily: "Lato",
                      p: "10px 5px",
                    }}
                  >
                    {isProcessing ? (
                      <Chip
                        label="Pending processing"
                        size="small"
                        sx={{
                          fontFamily: "Lato",
                          fontWeight: 700,
                          backgroundColor: "rgba(83, 109, 236, 0.12)",
                          color: "#536DEC",
                        }}
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Stack direction="row" spacing={2} mt={"20px"}>
        <ClientPlaidItemDelete
          customer_id={customer_id}
          plaiditem_id={item.id}
          setProfile={setProfile}
          removePlaidItemFromList={removePlaidItemFromList}
          customer={customer}
        />
        {hasError && (
          <LinkPlaidButton
            customer_id={customer_id}
            refreshList={refreshPlaidItemList}
            plaiditem_id={item.id}
            customer={customer}
            setProfile={setProfile}
          />
        )}
      </Stack>
    </Paper>
  );
};

export default function BankManagement({ customer_id, setProfile, customer }) {
  const [plaidItemList, setPlaidItemList] = useState([]);
  const [accountList, setAccountList] = useState([]);

  const context = React.useContext(Context);
  const isMobile = useMediaQuery("(max-width:1280px)");

  const topPadding = isMobile ? "140px" : "92px";

  useEffect(() => {
    let accessToken = AccessToken();
    if (accessToken === null) {
      window.location.href = "/login";
    } else {
      if (customer_id) {
        const getData = async () => {
          var plaidItemsResults = fetchPlaiditems(customer_id);
          var accountResults = fetchCustomerAccounts(
            customer_id,
            { type__name__in: "Bank,Credit Card", is_main_account: false },
            true
          );

          setPlaidItemList(await plaidItemsResults);
          setAccountList(await accountResults);
        };
        getData();
      }
    }
  }, [customer_id]);

  const refreshPlaidItemList = (id) => {
    if (id) {
      const getData = async () => {
        var plaidItemsResults = fetchPlaiditems(customer_id);

        setPlaidItemList(await plaidItemsResults);
      };
      getData();
    }
  };

  const removePlaidItemFromList = (plaid_item_id) => {
    setPlaidItemList((prevList) =>
      prevList.filter((plaid_item) => plaid_item.id !== plaid_item_id)
    );
    context.fireToast("Success", "plaid account deleted.", "success");
  };

  const updateLocalBankAccountLink = (
    bankAccountId,
    accountingAccountId,
    accountingAccountObj
  ) => {
    setPlaidItemList((prev) =>
      prev?.map((item) => ({
        ...item,
        bankaccount_set: item.bankaccount_set?.map((ba) =>
          ba.id === bankAccountId
            ? {
                ...ba,
                accounting_account:
                  accountingAccountObj ||
                  (accountingAccountId ? { id: accountingAccountId } : null),
              }
            : ba
        ),
      }))
    );
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Box padding={`${topPadding} 20px 20px 20px`}>
        {plaidItemList && plaidItemList.length > 0 ? (
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              backgroundColor: "white",
              borderRadius: 3,
              minHeight: "87vh",
            }}
          >
            <Box sx={{ pt: 3, px: 2, pb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="h5" fontWeight={600} mb={0.5}>
                    Plaid Management
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontSize="15px"
                  >
                    Link and manage all plaid accounts connected to the Lucro
                    System.
                  </Typography>
                </Box>

                <Box>
                  <LinkPlaidButton
                    customer_id={customer_id}
                    refreshList={refreshPlaidItemList}
                    customer={customer}
                    setProfile={setProfile}
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ padding: 2 }}>
              <Grid container spacing={2} wrap="wrap">
                {plaidItemList
                  .filter((item) => item.product === "transactions")
                  .map((item) => (
                    <Grid
                      item
                      key={item.id}
                      size={{ xs: 12, sm: 12, md: 6, lg: 4 }}
                      sx={{ display: "flex", flexDirection: "column" }}
                    >
                      <PlaidItem
                        item={item}
                        customer_id={customer_id}
                        accountList={accountList}
                        removePlaidItemFromList={removePlaidItemFromList}
                        refreshPlaidItemList={refreshPlaidItemList}
                        onLinked={updateLocalBankAccountLink}
                        setProfile={setProfile}
                        customer={customer}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Box>
          </Paper>
        ) : (
          <Box
            sx={{
              minHeight: "calc(80vh)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              gap: 2,
            }}
          >
            <img
              src="/missing-data.svg"
              alt="No plaid accounts linked"
              style={{ width: 150, height: 150 }}
            />
            <Typography
              variant="h6"
              fontWeight={500}
              fontSize={"28px"}
              sx={{ fontFamily: "Lato" }}
            >
              It’s looking empty here atm. Let’s get you up and running!
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              fontSize={"16px"}
              sx={{ fontFamily: "Lato" }}
            >
              Automate your income and track expenses by linking your plaid
              accounts. Click below and start your journey to financial success.
            </Typography>
            <Box mt={"30px"}>
              <LinkPlaidButton
                missing={true}
                customer_id={customer_id}
                refreshList={refreshPlaidItemList}
                customer={customer}
                setProfile={setProfile}
              />
            </Box>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
}
